import { auth } from "./auth";
import { API_BASE } from "./api";

const STORAGE_KEY = "artist_network_usage_v1";
const QUEUE_KEY = "artist_network_usage_queue_v1";

export type ArtistUsageEntry = {
  id: string | null;
  name: string;
  bytes: number;
};

export type ArtistUsageMap = Record<string, ArtistUsageEntry>;

// top-level store maps date (YYYY-MM-DD) -> ArtistUsageMap
export type ArtistUsageStore = Record<string, ArtistUsageMap>;

export type UsageQueueItem = {
  artistId: string | null;
  artistName: string;
  bytes: number;
  date: string; // YYYY-MM-DD
};

/**
 * Push an item to the sync queue in localStorage
 */
const pushToQueue = async (item: UsageQueueItem) => {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const q: UsageQueueItem[] = raw ? JSON.parse(raw) : [];
    q.push(item);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch (err) {
    console.warn("pushToQueue error", err);
  }
};

export const getUsageQueue = async (): Promise<UsageQueueItem[]> => {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.warn("getUsageQueue error", err);
    return [];
  }
};

export const clearUsageQueue = async () => {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify([]));
  } catch (err) {
    console.warn("clearUsageQueue error", err);
  }
};

const enqueueUsage = async (artistId: string | null, artistName: string, bytes: number) => {
  const today = new Date().toISOString().slice(0, 10);
  await pushToQueue({
    artistId,
    artistName,
    bytes: Math.max(0, Math.floor(bytes)),
    date: today,
  });
};

/**
 * Record usage for an artist. artistId may be null; in that case we key by name.
 */
export const addArtistUsage = async (
  artistId: string | number | null | undefined,
  artistName: string,
  bytes: number
) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const store: ArtistUsageStore = raw ? JSON.parse(raw) : {};
    const today = new Date().toISOString().slice(0, 10);
    const dayMap: ArtistUsageMap = store[today] || {};

    const idKey = artistId != null ? String(artistId) : `__name__:${artistName || "Unknown Artist"}`;
    const name = artistName || "Unknown Artist";
    const existing = dayMap[idKey] || { id: artistId != null ? String(artistId) : null, name, bytes: 0 };
    existing.bytes = (existing.bytes || 0) + Math.max(0, Math.floor(bytes));
    existing.name = name;
    dayMap[idKey] = existing;

    store[today] = dayMap;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));

    // Also enqueue for server batch telemetry sync
    try {
      await enqueueUsage(existing.id, existing.name, bytes);
    } catch {
      // ignore enqueue errors
    }
  } catch (err) {
    console.warn("addArtistUsage error", err);
  }
};

export const getArtistUsage = async (): Promise<ArtistUsageStore> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.warn("getArtistUsage error", err);
    return {};
  }
};

export const resetArtistUsage = async () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({}));
  } catch (err) {
    console.warn("resetArtistUsage error", err);
  }
};

export const getArtistUsageTotals = async (): Promise<Record<string, { artistId: string | null; artist: string; bytes: number }>> => {
  const store = await getArtistUsage();
  const totals: Record<string, { artistId: string | null; artist: string; bytes: number }> = {};
  Object.keys(store).forEach((date) => {
    const dayMap = store[date];
    Object.keys(dayMap).forEach((k) => {
      const e = dayMap[k];
      if (!totals[k]) totals[k] = { artistId: e.id ?? null, artist: e.name, bytes: 0 };
      totals[k].bytes += e.bytes || 0;
      totals[k].artist = e.name || totals[k].artist;
    });
  });
  return totals;
};

export const getArtistUsageList = async (): Promise<{ artistId: string | null; artist: string; bytes: number }[]> => {
  const totals = await getArtistUsageTotals();
  return Object.keys(totals)
    .map((k) => ({ artistId: totals[k].artistId, artist: totals[k].artist, bytes: totals[k].bytes }))
    .sort((a, b) => b.bytes - a.bytes);
};

export const getArtistUsageForDate = async (date: string): Promise<{ artistId: string | null; artist: string; bytes: number }[]> => {
  const store = await getArtistUsage();
  const dayMap = store[date] || {};
  return Object.keys(dayMap)
    .map((k) => ({ artistId: dayMap[k].id ?? null, artist: dayMap[k].name, bytes: dayMap[k].bytes }))
    .sort((a, b) => b.bytes - a.bytes);
};

/**
 * Flush usage queue to backend. Aggregates queued items into per-artist sums.
 */
export const flushUsageQueue = async (): Promise<boolean> => {
  try {
    const queue = await getUsageQueue();
    if (!queue || !queue.length) return true;

    // Aggregate
    const agg: Record<string, { artistId: string | null; artistName: string; bytes: number }> = {};
    queue.forEach((it) => {
      const key = it.artistId != null ? String(it.artistId) : `__name__:${it.artistName}`;
      agg[key] = agg[key] || { artistId: it.artistId, artistName: it.artistName, bytes: 0 };
      agg[key].bytes += it.bytes || 0;
    });

    const items = Object.keys(agg).map((k) => ({
      artist_id: agg[k].artistId,
      artist_name: agg[k].artistName,
      bytes: agg[k].bytes,
    }));

    const token = auth.access();
    const deviceId = auth.deviceId();
    const url = `${API_BASE}/usage/artist/batch/`;

    // Try sendBeacon for unauthenticated unload if available
    if (typeof navigator !== "undefined" && (navigator as any).sendBeacon && !token) {
      try {
        const payload = JSON.stringify({ items, device_id: deviceId });
        const blob = new Blob([payload], { type: "application/json" });
        const ok = (navigator as any).sendBeacon(url, blob);
        if (ok) {
          await clearUsageQueue();
          return true;
        }
      } catch {
        // fall through to fetch
      }
    }

    // Default: fetch with Authorization header or device_id
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ items, device_id: deviceId }),
      keepalive: true,
    });

    if (res && res.ok) {
      await clearUsageQueue();
      return true;
    } else {
      console.warn("flushUsageQueue failed", res?.status);
      return false;
    }
  } catch (err) {
    console.warn("flushUsageQueue error", err);
    return false;
  }
};

// Auto-flush timer
let _autoFlushHandle: number | null = null;

export const startAutoFlush = (intervalMs = 30_000) => {
  if (_autoFlushHandle) return;
  _autoFlushHandle = window.setInterval(() => {
    flushUsageQueue().catch(() => {});
  }, intervalMs);
};

export const stopAutoFlush = () => {
  if (_autoFlushHandle) {
    clearInterval(_autoFlushHandle);
    _autoFlushHandle = null;
  }
};

// Register browser unload handler
export const registerUnloadHandler = () => {
  if (typeof window === "undefined") return;

  const onUnload = () => {
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      const queue: UsageQueueItem[] = raw ? JSON.parse(raw) : [];
      if (!queue || !queue.length) return;

      const agg: Record<string, { artistId: string | null; artistName: string; bytes: number }> = {};
      queue.forEach((it) => {
        const key = it.artistId != null ? String(it.artistId) : `__name__:${it.artistName}`;
        agg[key] = agg[key] || { artistId: it.artistId, artistName: it.artistName, bytes: 0 };
        agg[key].bytes += it.bytes || 0;
      });

      const items = Object.keys(agg).map((k) => ({
        artist_id: agg[k].artistId,
        artist_name: agg[k].artistName,
        bytes: agg[k].bytes,
      }));

      const token = auth.access();
      const deviceId = auth.deviceId();
      const url = `${API_BASE}/usage/artist/batch/`;

      if ((navigator as any).sendBeacon && !token) {
        try {
          const payload = JSON.stringify({ items, device_id: deviceId });
          (navigator as any).sendBeacon(url, new Blob([payload], { type: "application/json" }));
          localStorage.setItem(QUEUE_KEY, JSON.stringify([]));
          return;
        } catch {
          // fall through
        }
      }

      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ items, device_id: deviceId }),
        keepalive: true,
      }).then(() => {
        localStorage.setItem(QUEUE_KEY, JSON.stringify([]));
      }).catch(() => {});
    } catch {
      // ignore on unload
    }
  };

  window.addEventListener("beforeunload", onUnload);
  return () => window.removeEventListener("beforeunload", onUnload);
};
